/* eslint-disable camelcase */
import { CreateListingInput, CreateListingResponse, logger } from '@sd/shared';
import { sdLegacyBrowserClient } from '../http/sdLegacyBrowserClient';

/**
 * This is the **OLD** SD user structure. It's not for
 * general use so it is marked as deprecated.
 *
 * @deprecated
 */
export type LegacySessionUser = {
  username: string;
  contact_name: string;
  contact_guid: string;
  online_role: string;
  seller_dashboard_admin: boolean;
  email: string;
  seller_numbers: number[];
  impersonating: boolean;
  impersonating_by_seller_number?: boolean;
  account_group_id?: number;
  beta_user_features?: string[];
};

export type LegacySessionRootUser = LegacySessionUser & { impersonator?: LegacySessionUser };
export type LegacySessionResponse = {
  session_user: LegacySessionRootUser;
  account_group_id?: string;
};

export const sdLegacySessionService = {
  /**
   * Gets the session information from a legacy SD login session. If no session exists, one will
   * automatically be created with the curent login user.
   */
  async getOrCreateSession(sessionId: string) {
    const response = await sdLegacyBrowserClient.post<LegacySessionResponse>('/api/create_session', {
      gateway_session_id: sessionId
    });
    return response.data;
  },

  /**
   * Set (or remove) impersonation user details on the legacy SD session.
   */
  async impersonate(sessionId: string, username: string | undefined) {
    await sdLegacyBrowserClient.post<void>('/api/impersonate', {
      gateway_session_id: sessionId,
      user_to_impersonate: username
    });
  },

  /**
   * Set (or remove) seller numbers on the legacy SD session.
   */
  async impersonateBySellerNumbers(sellerNumbers: number[], accountGroupId?: number) {
    await sdLegacyBrowserClient.post<void>('/api/impersonate_by_seller_numbers', {
      seller_numbers: sellerNumbers,
      account_group_id: accountGroupId
    });
  },

  /**
   * Set the auction_inventory_view flag in legacy
   */
  async impersonateByAuction(auctionId: string) {
    await sdLegacyBrowserClient.post<void>('/api/access_by_auction', {
      auction_name: auctionId
    });
  },

  /**
   * Create listings
   *
   * Currently only creates one listing - bulk create will be handled later
   */
  async createListings(inputs: CreateListingInput[]) {
    logger.info('ListingCreationStarted', {
      input: inputs[0]
    });

    const { data: response } = await sdLegacyBrowserClient.post<CreateListingResponse>(
      '/api/create_listing',
      inputs[0]
    );
    const filteredErrors = response.listing.errors
      ? response.listing.errors.filter(
          error => !error.toUpperCase().includes('YOU CAN NOT ACTIVATE THE LISTING UNTIL AN IMAGE HAS BEEN ASSIGNED')
        )
      : [];
    if (response.listing.status?.toUpperCase() === 'INCOMPLETE' && filteredErrors.length > 0) {
      logger.error('ListingCreationIncomplete', {
        unitHref: inputs[0]?.href,
        vin: inputs[0]?.vin,
        type: inputs[0]?.listing_options.listing_type,
        listingId: response.listing.id,
        errorMessage: response.listing.errors
      });

      throw new Error(filteredErrors[0]);
    }

    logger.info('ListingCreatedSuccess', {
      unitHref: inputs[0]?.href,
      vin: inputs[0]?.vin,
      type: inputs[0]?.listing_options.listing_type,
      listingId: response.listing.id
    });
    return response;
  }
};
